import {
  computeCollapseState,
  computeIsStickyHeaderVisible,
  computeIsVirtualizationEnabled,
  computeShowFooter,
  computeShowFormulaBar,
} from 'notebook/tables/store/logic/TableVisibilityLogic';
import {createTableStoreSelector} from 'notebook/tables/store/selectors/util/SelectorCreators';
import {TableEditableStore} from 'notebook/tables/store/TableStore';
import TableUtil from 'notebook/tables/TableUtil';

import {TableSelectionModel} from './TableSelectionSelectors';

/**
 * Model containing properties that control which parts of the table UI are visible.
 */
export interface TableVisibilityModel {
  /**
   * Whether the table footer should be rendered.
   * From the legacy `shouldRenderFooter()`.
   */
  showFooter: boolean;

  /**
   * Whether the formula bar should be visible.
   * From the legacy `isFormulaBoxVisible()`.
   */
  showFormulaBar: boolean;

  /**
   * The effective collapsed state of the table.
   * From the legacy `useTableCollapse().isCollapsed`.
   */
  isCollapsed: boolean;

  /**
   * Whether the table should be collapsed on its first render.
   * From the legacy `useTableCollapse().isCollapsedInitially`.
   */
  isCollapsedInitially: boolean;

  /**
   * Whether the table is perma-collapsed (exported as CSV in a read-only view).
   * From the legacy `useTableCollapse().isPermaCollapsed`.
   */
  isPermaCollapsed: boolean;

  /**
   * Whether virtualization (windowed rendering) is enabled for the table.
   * Disabled in viewer mode and for tables below the row threshold.
   */
  isVirtualizationEnabled: boolean;

  /**
   * Whether the sticky header is currently being tracked and displayed (the
   * table is scrolled past its header with an active multi-selection).
   */
  isStickyHeaderVisible: boolean;
}

/**
 * Creates a memoized selector that computes the TableVisibilityModel from the store.
 */
export function createTableVisibilitySelector(
  tableSelectionSelector: (store: TableEditableStore) => TableSelectionModel
): (store: TableEditableStore) => TableVisibilityModel {
  return createTableStoreSelector(
    'tableVisibility',
    {
      hideFooter: (store) => store.stableProps.hideFooter ?? false,
      viewer: (store) => store.stableProps.viewer ?? false,
      isSingleInputView: (store) => store.stableProps.isSingleInputView ?? false,
      showFormulaBox: (store) => store.stableProps.showFormulaBox,
      firstRowLength: (store) => store.volatileProps.computedTable[0]?.length ?? 0,
      table: (store) => store.volatileProps.table,
      isCollapsible: (store) => store.stableProps.isCollapsible ?? false,
      autoCollapseLimit: (store) => store.stableProps.autoCollapseLimit,
      forceCollapsed: (store) => store.stableProps.forceCollapsed ?? false,
      isFocused: (store) => store.stableProps.isFocused ?? false,
      isMaximized: (store) => store.stableProps.isMaximized ?? false,
      isSplitScreen: (store) => store.stableProps.isSplitScreen ?? false,
      collapsedStateInRedux: (store) => store.reduxState.collapsedStateInRedux,
      canShowStickyHeader: (store) => store.state.canShowStickyHeader,
      hasStickyHeaderPositioning: (store) => store.state.stickyHeaderPositioning !== undefined,
      hasMultiSelection: (store) => tableSelectionSelector(store).hasMultiSelection,
    },
    ({
      hideFooter,
      viewer,
      isSingleInputView,
      showFormulaBox,
      firstRowLength,
      table,
      isCollapsible,
      autoCollapseLimit,
      forceCollapsed,
      isFocused,
      isMaximized,
      isSplitScreen,
      collapsedStateInRedux,
      canShowStickyHeader,
      hasStickyHeaderPositioning,
      hasMultiSelection,
    }) => {
      const {rowCount, cellCount} = TableUtil.getTableDimensionsFromTable(table);
      const {isCollapsed, isCollapsedInitially, isPermaCollapsed} = computeCollapseState({
        rowCount,
        cellCount,
        tableKind: TableUtil.getTableKind(table),
        isLegacyTable: TableUtil.isLegacyTable(table._tableType),
        isCollapsible,
        autoCollapseLimit,
        viewer,
        forceCollapsed,
        isFocused,
        isMaximized,
        isSplitScreen,
        collapsedStateInRedux,
      });
      return {
        showFooter: computeShowFooter(hideFooter, viewer, isSingleInputView),
        showFormulaBar: computeShowFormulaBar(showFormulaBox, viewer, isCollapsed, firstRowLength),
        isCollapsed,
        isCollapsedInitially,
        isPermaCollapsed,
        isVirtualizationEnabled: computeIsVirtualizationEnabled(viewer, table.table.length),
        isStickyHeaderVisible: computeIsStickyHeaderVisible(
          canShowStickyHeader,
          hasStickyHeaderPositioning,
          hasMultiSelection
        ),
      };
    }
  );
}
